import { Injectable } from '@angular/core';
import {
  ChatSettings,
  ChatSettingsRanges,
  DeploymentAdminModel,
  DeploymentModel,
  ModelService,
} from 'digiteq-ai-portal-client-lib';
import { map, Observable, of, Subject } from 'rxjs';

const LOCAL_STORAGE_SETTINGS = 'dq-chat-settings'
const LOCAL_STORAGE_PERSONA = 'dq-chat-persona'
const LOCAL_STORAGE_HISTORY_VISIBLE = 'dq-chat-history-visible'
const LOCAL_STORAGE_SETTINGS_VISIBLE = 'dq-chat-settings-visible'

@Injectable({
  providedIn: 'root'
})
export class AppSettingsService {
  $settingsChange = new Subject<ChatSettings>();

  private _selectedModel: DeploymentAdminModel;
  private _hideSettings: boolean;
  private _models: DeploymentModel[];

  get models(): DeploymentModel[] {
    return this._models;
  }

  set models(value: DeploymentModel[]) {
    this._models = value;
  }

  get selectedModel(): DeploymentAdminModel {
    return this._selectedModel;
  }

  set selectedModel(value: DeploymentAdminModel) {
    this._selectedModel = value;
  }

  get hideSettings(): boolean {
    return this._hideSettings;
  }

  set hideSettings(value: boolean) {
    this._hideSettings = value;
  }

  get savedSettings(): ChatSettings | null {
    const storageSettings = localStorage.getItem(LOCAL_STORAGE_SETTINGS);
    if (!storageSettings) {
      return null;
    }
    return JSON.parse(storageSettings)
  }

  set savedSettings(value: ChatSettings) {
    localStorage.setItem(LOCAL_STORAGE_SETTINGS, JSON.stringify(value));
    this.$settingsChange.next(value)
  }

  get historyVisible(): boolean {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_HISTORY_VISIBLE)) ?? false;
  }

  set historyVisible(value: boolean) {
    localStorage.setItem(LOCAL_STORAGE_HISTORY_VISIBLE, JSON.stringify(value));
  }

  get settingsVisible(): boolean {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_SETTINGS_VISIBLE)) ?? false;
  }

  set settingsVisible(value: boolean) {
    localStorage.setItem(LOCAL_STORAGE_SETTINGS_VISIBLE, JSON.stringify(value));
  }

  get savedPersonaId(): number | null {
    const storagePersona = localStorage.getItem(LOCAL_STORAGE_PERSONA);
    if (!storagePersona) {
      return null;
    }
    return +storagePersona;
  }

  set savedPersonaId(id: number) {
    localStorage.setItem(LOCAL_STORAGE_PERSONA, id.toString());
  }

  constructor(private readonly modelService: ModelService) {}

  getInitialSettings(): Observable<ChatSettings> {
    const localSettings = localStorage.getItem(LOCAL_STORAGE_SETTINGS)
    const chatSettings = JSON.parse(localSettings);
    if (chatSettings?.deploymentId) {
      return of(chatSettings);
    }
    return this.getDefaultSettings();
  }

  getDefaultSettings(): Observable<ChatSettings> {
    return this.modelService.getDefaultSettings().pipe(
      map((savedSettings) => {
        this.savedSettings = savedSettings
        return savedSettings
    }))
  }

  getSettingsRanges(): Observable<ChatSettingsRanges> {
    return this.modelService.getSettingsRanges();
  }

  getSelectedModelName(): string {
    return this.selectedModel.modelFriendlyName;
  }

  getModels() {
    return this.modelService.getAllModels();
  }

  selectModel(id: number) {
    this.selectedModel = this.models.find((model) => model.id === id);
  }
}
